import { DiversificationItem } from '@/store/diversificationsApi';
import { HoldingViewModel } from '@/store/holdingsApi';
import HoldingDetailsFlyout from '@/components/flyouts/HoldingDetailsFlyout';
import { formatNumberToCurrency } from '@/utils/currency';
import { convertNumberToPercentage } from '@/utils/numbers';
import { percentageBreakdown } from '@/utils/mathHelper';
import { holdingAccountLabel } from '@/utils/holdingAccountLabel';
import { mobileWidth } from '@/constants';
import { PieChart } from '@mui/x-charts/PieChart';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
  DataGridPremium,
  GridCellParams,
  GridColDef,
  GridListViewColDef,
} from '@mui/x-data-grid-premium';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GridContainer } from '../../../components/grids/GridContainer';

type HoldingsRow = DiversificationItem & {
  recordIndex: number;
  allocationPercentage: number;
};

interface AssetAllocationHoldingsGridProps {
  records: DiversificationItem[];
  categoryTitle: string;
  graphColors: string[];
  isLoading: boolean;
  endDate?: string;
  /**
   * Forces mobile/list-view layout regardless of viewport width.
   * Pass `true` when the widget container width is below the XS breakpoint.
   */
  forceMobile?: boolean;
  /**
   * Suppresses the holding detail flyout. Set by the advisor mount: the flyout's
   * queries carry no investor scope, so opening it would read unscoped data.
   */
  disableHoldingDetails?: boolean;
  /** Forwarded to the internal GridContainer so a dashboard tile gets the widget height instead of the full-page one. */
  isWidget?: boolean;
}

const PIE_SIZE = 20;

const COL_MIN_WIDTH_HOLDING = 150;
const COL_WIDTH_PERCENTAGE = 230;
const COL_WIDTH_BALANCE = 160;
const CELL_ICON_GAP = 2;
const TITLE_BOTTOM_MARGIN = 3;
const COL_HEADER_HEIGHT_TWO_LINES = 60;

const MiniPie: React.FC<{ value: number; color: string }> = ({
  value,
  color,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const pieEmptyColor = isDarkMode
    ? 'rgba(255,255,255,0.12)'
    : 'rgba(0,0,0,0.12)';
  const clampedValue = Math.max(0, Math.min(1, value));
  return (
    <PieChart
      series={[
        {
          data: [
            { value: clampedValue, color },
            { value: 1 - clampedValue, color: pieEmptyColor },
          ],
          innerRadius: 0,
          outerRadius: PIE_SIZE / 2,
          cx: PIE_SIZE / 2,
          cy: PIE_SIZE / 2,
        },
      ]}
      width={PIE_SIZE}
      height={PIE_SIZE}
      margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
      hideLegend
      slots={{ tooltip: () => null }}
    />
  );
};

/**
 * A holding's name with the account it sits in beneath it, so lots of one security held in
 * different accounts can be told apart. Category rows carry no account and show the name alone.
 */
const HoldingNameCell: React.FC<{ row: HoldingsRow }> = ({ row }) => {
  const accountLabel = holdingAccountLabel(row);
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        height: '100%',
        minWidth: 0,
        py: 1,
      }}
    >
      <Typography
        variant='body2'
        sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
      >
        {row.name}
      </Typography>
      {accountLabel && (
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
        >
          {accountLabel}
        </Typography>
      )}
    </Box>
  );
};

const SORT_BY_BALANCE_DESC = {
  sorting: { sortModel: [{ field: 'y', sort: 'desc' as const }] },
};

/** Category bucket rows have no AccountName; only real holding rows do. */
const isHoldingRow = (row: HoldingsRow): boolean =>
  typeof row.AccountName === 'string' && row.AccountName.length > 0;

const buildHoldingFromRow = (row: HoldingsRow): HoldingViewModel => ({
  HoldingId: row.id,
  Name: row.name,
  AccountName: row.AccountName ?? '',
  AccountId: 0,
  InstitutionName: row.InstitutionName ?? '',
  InstitutionId: 0,
  Balance: row.y,
  Quantity: row.Quantity,
  Price: row.Price,
  PercentOfAccount: 0,
  Classifications: '',
  WealthHoldingType: '',
  UpdatedDate: '',
  CreatedDate: '',
  HasTaxLots: false,
  CanEditCostBasis: false,
  WealthType: 0,
});

const AssetAllocationHoldingsGrid: React.FC<
  AssetAllocationHoldingsGridProps
> = ({
  records,
  categoryTitle,
  graphColors,
  isLoading,
  endDate,
  forceMobile = false,
  disableHoldingDetails = false,
  isWidget = false,
}) => {
  const { t } = useTranslation();
  const isXs = useMediaQuery(mobileWidth) || forceMobile;
  const [selectedHolding, setSelectedHolding] =
    useState<HoldingViewModel | null>(null);

  const handleHoldingSelect = useCallback(
    (row: HoldingsRow) => {
      if (disableHoldingDetails) return;
      if (!isHoldingRow(row)) return;
      setSelectedHolding(buildHoldingFromRow(row));
    },
    [disableHoldingDetails],
  );

  const rows: HoldingsRow[] = useMemo<HoldingsRow[]>(() => {
    const totalBalance = records.reduce((sum, record) => sum + record.y, 0);
    const percentages = percentageBreakdown(
      records.map((record) => record.y),
      totalBalance,
    );
    return records.map((record, index) => ({
      ...record,
      recordIndex: index,
      allocationPercentage: percentages[index],
    }));
  }, [records]);

  const columns = useMemo<GridColDef<HoldingsRow>[]>(
    () => [
      {
        field: 'name',
        headerName: t('grid.holding'),
        flex: 1,
        minWidth: COL_MIN_WIDTH_HOLDING,
        renderCell: (params) => <HoldingNameCell row={params.row} />,
      },
      {
        field: 'allocationPercentage',
        renderHeader: () => (
          <Box
            sx={{ whiteSpace: 'normal', lineHeight: 1.2, textAlign: 'right' }}
          >
            {`${t('assetAllocation.percentOf')} ${categoryTitle}`}
          </Box>
        ),
        type: 'number',
        width: COL_WIDTH_PERCENTAGE,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value) => convertNumberToPercentage(value),
        renderCell: (params) => {
          const percentage = params.value as number;
          const color =
            graphColors[params.row.recordIndex % graphColors.length];
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: CELL_ICON_GAP,
                justifyContent: 'flex-end',
                width: '100%',
                height: '100%',
              }}
            >
              <Typography variant='body2'>
                {convertNumberToPercentage(percentage)}
              </Typography>
              <Box sx={{ ml: '-5px', display: 'flex', alignItems: 'center' }}>
                <MiniPie
                  value={percentage}
                  color={color}
                />
              </Box>
            </Box>
          );
        },
      },
      {
        field: 'y',
        headerName: t('grid.balance'),
        type: 'number',
        width: COL_WIDTH_BALANCE,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value) => formatNumberToCurrency(value),
      },
    ],
    [t, categoryTitle, graphColors],
  );

  const listViewColDef = useMemo<GridListViewColDef<HoldingsRow>>(
    () => ({
      field: 'listColumn',
      renderCell: (params) => {
        const row = params.row;
        return (
          <Box
            sx={{ width: '100%', py: 2, cursor: 'pointer' }}
            onClick={() => {
              handleHoldingSelect(row);
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
                width: '100%',
              }}
            >
              <Typography
                variant='body2'
                sx={{
                  fontWeight: '600 !important',
                  color: 'text.primary',
                  flex: 1,
                  minWidth: 0,
                  whiteSpace: 'normal',
                }}
              >
                {row.name}
              </Typography>
              <Typography
                variant='body2'
                sx={{
                  fontWeight: '600 !important',
                  color: 'text.primary',
                  whiteSpace: 'nowrap',
                  textAlign: 'right',
                }}
              >
                {formatNumberToCurrency(row.y)}
              </Typography>
            </Box>
            {holdingAccountLabel(row) && (
              <Typography
                variant='caption'
                sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}
              >
                {holdingAccountLabel(row)}
              </Typography>
            )}
            <Typography
              variant='body2'
              sx={{ fontWeight: 400, color: 'text.secondary', mt: 0.5 }}
            >
              {t('assetAllocation.percentOfCategory', {
                category: categoryTitle,
                value: convertNumberToPercentage(row.allocationPercentage),
              })}
            </Typography>
          </Box>
        );
      },
    }),
    [t, categoryTitle, handleHoldingSelect],
  );

  if (isLoading) {
    return (
      <GridContainer isWidget={isWidget}>
        <DataGridPremium
          key={isXs ? 'list-view' : 'grid-view'}
          rows={[]}
          columns={columns}
          loading
          hideFooter
          columnHeaderHeight={COL_HEADER_HEIGHT_TWO_LINES}
          initialState={SORT_BY_BALANCE_DESC}
          {...(isXs ? { listView: true, listViewColumn: listViewColDef } : {})}
        />
      </GridContainer>
    );
  }

  return (
    <Box width='100%'>
      <Typography
        variant='h6'
        mb={TITLE_BOTTOM_MARGIN}
      >
        {t('assetAllocation.holdings')} - {categoryTitle}
      </Typography>

      <GridContainer isWidget={isWidget}>
        <DataGridPremium
          // Remount on layout switch so MUI's cached row heights / list-view
          // state don't leak between mobile (auto height) and desktop (default).
          key={isXs ? 'list-view' : 'grid-view'}
          rows={rows}
          columns={columns}
          getRowId={(row) => row.recordIndex}
          disableRowSelectionOnClick
          hideFooter
          initialState={SORT_BY_BALANCE_DESC}
          onCellClick={(params: GridCellParams<HoldingsRow>) => {
            if (params.field === 'name' && isHoldingRow(params.row)) {
              handleHoldingSelect(params.row);
            }
          }}
          getRowHeight={() => 'auto'}
          {...(isXs
            ? {
                listView: true,
                listViewColumn: listViewColDef,
              }
            : {})}
          sx={{
            '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
            '& .MuiDataGrid-cell--textRight': { justifyContent: 'flex-end' },
            '& .MuiDataGrid-cell[data-field="name"]': {
              cursor: disableHoldingDetails ? 'default' : 'pointer',
            },
          }}
        />
      </GridContainer>
      {selectedHolding && (
        <HoldingDetailsFlyout
          open={!!selectedHolding}
          toggleDrawer={() => {
            setSelectedHolding(null);
          }}
          holding={selectedHolding}
          endDate={endDate}
        />
      )}
    </Box>
  );
};

export default AssetAllocationHoldingsGrid;
